import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';

type Props = {
  children: ReactNode;
  className?: string;
  fadeClassName?: string;
  wrapperClassName?: string;
};

export default function ScrollFade({
  children,
  className,
  fadeClassName,
  wrapperClassName
}: Props) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [fadeLeft, setFadeLeft] = useState(false);
  const [fadeRight, setFadeRight] = useState(false);

  const measure = useCallback(() => {
    const element = scrollerRef.current;

    if (!element) return;

    const { clientWidth, scrollLeft, scrollWidth } = element;

    setFadeLeft(scrollLeft > 1);
    setFadeRight(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  useEffect(measure);

  useEffect(() => {
    const element = scrollerRef.current;

    if (!element || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(measure);

    observer.observe(element);

    return () => observer.disconnect();
  }, [measure]);

  return (
    <div className={`relative ${wrapperClassName ?? ''}`}>
      <div
        className={`no-scrollbar overflow-x-auto ${className ?? ''}`}
        onScroll={measure}
        ref={scrollerRef}
      >
        {children}
      </div>
      {fadeLeft && (
        <span
          className={`pointer-events-none absolute inset-y-0 left-0 w-10 bg-gradient-to-r to-transparent ${fadeClassName ?? ''}`}
        />
      )}
      {fadeRight && (
        <span
          className={`pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l to-transparent ${fadeClassName ?? ''}`}
        />
      )}
    </div>
  );
}
